import React, {MouseEvent} from "react";
import clsx from "clsx";
import {
    encodeUnicode,
    getEntryUrl,
    stripTags,
    translate,
    useMediaQuery,
} from "@mediaspace/shared/utils";
import { useTheme } from "@mediaspace/shared/styled";
import {BackgroundThumbnail} from "@mediaspace/shared/ui";
import {Button, Tooltip} from "@kaltura/ds-react-components";
import { KmsTypePresenter, StaticImage } from "@mediaspace/shared/types";
import { PreviewPlayer } from "@mediaspace/player";
import { Lines } from "./Lines";
import { EventPresentersList } from "@mediaspace/event-platform/event-presenter";
import {
    Content,
    Description,
    LiveLabel,
    Name,
    StyledBackDrop,
    StyledHeroUnit,
    Title,
    TitleWrapper,
    TogglePlayButton,
    ButtonsWrapper
} from "./styled-components";
import {useButtonAnalytics} from "@mediaspace/hooks";
import {ButtonClickAnalyticsType} from "@mediaspace/shared/types/ButtonClickAnalyticsType";
import ReactHtmlParser from "@mediaspace/shared/ui/html-react-parser";
import TruncateMarkup from "react-truncate-markup";

export interface HeroUnitProps {
    /**
     * additional classname to add to the root div
     */
    className?: string;

    /**
     * current entry id, required when not showing static images
     */
    id?: string;

    /**
     * current entry name, used in entry links
     */
    entryName?: string;

    /**
     * current entry/item name to be displayed
     */
    name: string;

    isLive: boolean;

    /**
     * page title to show above item details
     */
    title: string;

    presenters?: KmsTypePresenter[];
    description: string;
    currentItemIndex: number;
    mediaCount: number;
    entryCarouselInterval: number;
    currentTime: number;

    /**
     * is playback active
     */
    isActive: boolean;
    playerMedia: { entryId: string; ks?: string };
    customizedPlayerConfig: Record<string, unknown>;
    // eslint-disable-next-line
    onPlayerReadyHandler: (playerInstance: any) => void;
    toggleIsActiveClickHandler: (
        ev: React.MouseEvent<HTMLButtonElement>
    ) => void;
    onLineClick: (args: { index: number }) => void;

    /**
     * image to show while player playback unavailable
     */
    thumbnailUrl: string;

    /**
     * current static image info.
     * Static images gets priority over entries. Decision about which type to display (images or entries)
     * should be done on server side.
     */
    staticImage?: StaticImage;

    /**
     * names of all items in carousel
     */
    itemsNames?: string[];
}

export const HeroUnit: React.FC<HeroUnitProps> = ({
    className,
    id,
    entryName,
    name,
    isLive,
    entryCarouselInterval,
    title,
    onLineClick,
    presenters,
    currentTime,
    description,
    currentItemIndex,
    mediaCount,
    isActive,
    playerMedia,
    customizedPlayerConfig,
    toggleIsActiveClickHandler,
    onPlayerReadyHandler,
    thumbnailUrl,
    staticImage,
    itemsNames = [],
}) => {
    const theme = useTheme();
    const smallScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const playButtonText = !staticImage ? translate("Play Preview") : translate("Play carousel");
    const pauseButtonText = !staticImage ? translate("Pause Preview") : translate("Pause carousel");

    const sendButtonAnalytics = useButtonAnalytics();

    const handleHeroClick = (event: MouseEvent<HTMLElement>) => {
        const target = event.target as HTMLInputElement;
        const parent = target.parentElement as HTMLInputElement;
        if (target.type === "button" || parent.type  === "button") {
            sendButtonAnalytics(`Hero - ${isLive ? "Live Now" : "Watch Now"}`, ButtonClickAnalyticsType.NAVIGATE);
        } else {
            sendButtonAnalytics("Hero - Media Selected", ButtonClickAnalyticsType.THUMBNAIL);
        }
    }

    return (
        <StyledHeroUnit
            className={clsx("kms-ds-homepage-component-hero-unit", className)}
            href={
                !staticImage
                    ? getEntryUrl(id, undefined, entryName)
                    : staticImage.actionUrl
            }
            aria-label={translate("Go to media %1",[name])}
            tabIndex={-1}
            data-analytics={encodeUnicode(name)}
            onClick={handleHeroClick}
        >
            {!staticImage ? (
                <PreviewPlayer
                    className={"kms-ds-homepage-component-hero-unit-preview"}
                    playerClassName={
                        "kms-ds-homepage-component-hero-unit-player"
                    }
                    active={isActive}
                    media={playerMedia}
                    playerConfig={customizedPlayerConfig}
                    onReady={onPlayerReadyHandler}
                >
                    <BackgroundThumbnail
                        thumbnailUrl={thumbnailUrl}
                        className={"kms-ds-homepage-component-hero-unit-img"}
                    />
                </PreviewPlayer>
            ) : (
                <BackgroundThumbnail
                    thumbnailUrl={staticImage.imageUrl}
                    className={"kms-ds-homepage-component-hero-unit-img"}
                    alt={staticImage.alt}
                />
            ) }

            <StyledBackDrop className="kms-ds-homepage-component-hero-unit-backdrop" />
            {title && (
                <TitleWrapper>
                    <Title
                        variant={"h1"}
                        className={"kms-ds-homepage-component-hero-unit-title"}
                    >
                        {ReactHtmlParser(title)}
                    </Title>
                </TitleWrapper>
            )}

            {/* as it is rendered inside link element, component needs to have application role
             in order put screen reader into 'forms' mode and enable the interactive buttons to work properly*/}
            <Content role="application" className="kms-ds-homepage-component-hero-unit-content">
                {isLive && (
                    <LiveLabel>
                        {translate("Live")}
                    </LiveLabel>
                )}

                {name && (
                    <Name variant={!smallScreen ? "h1" : "h2"}>
                        <Tooltip title={name}>
                            <span className="kms-ds-homepage-component-hero-unit-name">
                                <TruncateMarkup lines={2}>
                                    <span>{name}</span>
                                </TruncateMarkup>
                            </span>
                        </Tooltip>
                    </Name>
                )}

                {!isLive && description && (
                    <Description
                        className={
                            "kms-ds-homepage-component-hero-unit-description"
                        }
                    >
                        <TruncateMarkup lines={4}>
                            <span>{stripTags(description)}</span>
                        </TruncateMarkup>
                    </Description>
                )}

                {isLive && (
                    <EventPresentersList
                        presenters={presenters}
                        isTranslucent={true}
                        className={
                            "kms-ds-homepage-component-hero-unit-presenters"
                        }
                        analyticsDescription={"Hero unit"}
                        entryId={id}
                    />
                )}

                <ButtonsWrapper>
                    {!staticImage ? (
                        <Button
                            className={
                                "kms-ds-homepage-component-hero-unit-join-button"
                            }
                            color={isLive ? "danger" : "primary"}
                            type={"button"}
                            size={"large"}
                            title={translate("Visit media page %1", [name])}
                        >
                            {isLive
                                ? translate("Join Now")
                                : translate("Watch Now")}
                        </Button>
                    ) : (
                        staticImage.actionUrl && (
                            <Button
                                href={staticImage.actionUrl}
                                color={"primary"}
                                size={"large"}
                            >
                                {staticImage.actionText}
                            </Button>
                        )
                    )}

                    <TogglePlayButton
                        type={"button"}
                        variant={"borderless"}
                        className={
                            "kms-ds-homepage-component-hero-unit-toggle-play-button"
                        }
                        onClick={(ev: MouseEvent<HTMLButtonElement>) => {
                            sendButtonAnalytics(`Hero - ${isActive ? "Pause" : "Play"} Preview`, ButtonClickAnalyticsType.TOGGLE);
                            toggleIsActiveClickHandler(ev);
                        }}
                        aria-label={isActive ? translate("Stop playing carousel preview") : translate("Start playing carousel preview")}
                    >
                        {isActive
                            ? pauseButtonText
                            : playButtonText}
                    </TogglePlayButton>
                </ButtonsWrapper>

            </Content>

            {mediaCount > 1 && (
                <Lines
                    className="kms-ds-homepage-component-hero-unit-lines"
                    currentItemIndex={currentItemIndex}
                    onLineClick={onLineClick}
                    currentTime={currentTime}
                    entryCarouselInterval={entryCarouselInterval}
                    itemsNames={itemsNames}
                />
            )}
        </StyledHeroUnit>
    );
};

export default HeroUnit;
